import { cn } from '@/shared/lib/cn';

export interface CheckboxProps {
  checked: boolean;
  onChange: () => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Checkbox 14px do Figma FGP (borda gray-text-aux, preenchida em brand-600 quando marcada).
 * Usa `<button role="checkbox">` para não depender do estilo nativo do navegador.
 */
export function Checkbox({ checked, onChange, label, disabled, className }: CheckboxProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'flex size-3.5 shrink-0 items-center justify-center rounded-[2px] border transition-colors',
        checked ? 'border-brand-outline bg-brand-solid text-white' : 'border-ink-muted bg-surface',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
          <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

export interface CheckboxLabelProps extends CheckboxProps {
  /** Texto exibido ao lado do checkbox (Figma: "Checkbox + text"). */
  text: string;
  bold?: boolean;
}

/** Checkbox + rótulo clicável, como no Figma ("Checkbox + text"). */
export function CheckboxLabel({ text, bold, checked, onChange, disabled, className }: CheckboxLabelProps) {
  return (
    <label
      className={cn(
        'flex select-none items-center gap-2',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'flex size-3.5 shrink-0 items-center justify-center rounded-[2px] border transition-colors',
          checked ? 'border-brand-outline bg-brand-solid text-white' : 'border-ink-muted bg-surface',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-outline',
        )}
      >
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className={cn('text-sm text-ink', bold && checked && 'font-semibold')}>{text}</span>
    </label>
  );
}
